import bidiFactory, { type BidiInstance } from 'bidi-js';
import { flow } from 'lodash';
import type {
  ParsedSegment,
  StringParser,
  StringParserParseOptions,
  TextSegmentParser,
} from './types';
import { normalizeSegmentsToLines } from './normalizeSegmentsToLines';
import { parseGrapheme } from './parseGrapheme';
import { parseWord } from './parseWord';

type Params = {
  locale: string;
  granularity: 'character' | 'word';
  normalizers?: Array<(segments: Array<ParsedSegment>) => Array<ParsedSegment>>;
};

const scopeToGranularityMap = {
  character: 'grapheme',
  word: 'word',
} as const;

type TextSegmentGranularity =
  (typeof scopeToGranularityMap)[keyof typeof scopeToGranularityMap];

const granularityToParserMap: Record<
  TextSegmentGranularity,
  TextSegmentParser
> = {
  grapheme: parseGrapheme,
  word: parseWord,
} as const;

let bidi: BidiInstance | undefined;

export const createStringParser = (params: Params): StringParser => {
  const { locale, granularity: _granularity, normalizers = [] } = params;

  const granularity = scopeToGranularityMap[_granularity];

  if (!granularity) {
    throw new Error('StringParser: granularity is undefined!');
  }

  if (!bidi) {
    bidi = bidiFactory();
  }

  const bidiInstance = bidi;

  const segmenter = new Intl.Segmenter(locale, {
    granularity,
  });

  const normalize = flow(...normalizers, normalizeSegmentsToLines);

  const defaultParseOptions: StringParserParseOptions = {
    textDirection: 'ltr',
  };

  return {
    parse: (str, options) => {
      const { textDirection } = options ?? defaultParseOptions;
      const parser = granularityToParserMap[granularity];
      return parser(str, textDirection, segmenter, normalize, bidiInstance);
    },
  };
};
